import { useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { useAuthStore } from '../hooks/useAuth'
import {
  Play, Clock, Users, Star, BookOpen, CheckCircle, Lock,
  Download, MessageSquare, Award, ArrowLeft, FileText
} from 'lucide-react'

interface Lesson {
  id: string
  title: string
  duration: string
  type: 'video' | 'quiz' | 'document'
  completed: boolean
  locked: boolean
}

interface CourseData {
  id: string
  title: string
  description: string
  instructor: string
  thumbnail: string
  lessons: Lesson[]
  totalStudents: number
  rating: number
  progress: number
}

export default function CourseDetail() {
  const { id } = useParams()
  const { user } = useAuthStore()
  const [activeLesson, setActiveLesson] = useState<string | null>(null)
  const [showQuiz, setShowQuiz] = useState(false)

  const course: CourseData = {
    id: id || '1',
    title: 'Advanced Mathematics',
    description: 'Master calculus, linear algebra, and differential equations with hands-on exercises and real-world applications.',
    instructor: 'Dr. Sarah Johnson',
    thumbnail: 'https://images.unsplash.com/photo-1635070041078-e363dbe005cb?w=800',
    totalStudents: 1234,
    rating: 4.8,
    progress: 65,
    lessons: [
      { id: 'l1', title: 'Introduction to Calculus', duration: '15:30', type: 'video', completed: true, locked: false },
      { id: 'l2', title: 'Limits and Continuity', duration: '22:45', type: 'video', completed: true, locked: false },
      { id: 'l3', title: 'Quiz: Limits Basics', duration: '10:00', type: 'quiz', completed: true, locked: false },
      { id: 'l4', title: 'Derivatives Fundamentals', duration: '28:15', type: 'video', completed: false, locked: false },
      { id: 'l5', title: 'Quiz: Derivatives', duration: '12:00', type: 'quiz', completed: false, locked: false },
      { id: 'l6', title: 'Integration Techniques', duration: '35:00', type: 'video', completed: false, locked: true },
      { id: 'l7', title: 'Linear Algebra Basics', duration: '40:20', type: 'video', completed: false, locked: true },
      { id: 'l8', title: 'Final Assessment', duration: '45:00', type: 'quiz', completed: false, locked: true },
    ]
  }

  const activeLessonData = course.lessons.find(l => l.id === activeLesson)

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Link to="/courses" className="p-2 hover:bg-slate-800 rounded-lg transition-colors">
          <ArrowLeft className="w-5 h-5" />
        </Link>
        <div>
          <h1 className="text-2xl font-bold">{course.title}</h1>
          <p className="text-slate-500">by {course.instructor}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-4">
          {/* Video Player / Content Area */}
          <div className="glass rounded-xl overflow-hidden">
            {activeLessonData ? (
              activeLessonData.type === 'video' ? (
                <div className="aspect-video bg-slate-900 flex items-center justify-center relative">
                  <img src={course.thumbnail} className="w-full h-full object-cover opacity-50" alt="" />
                  <button className="absolute w-20 h-20 bg-primary/90 rounded-full flex items-center justify-center hover:scale-110 transition-transform">
                    <Play className="w-8 h-8 text-white ml-1" />
                  </button>
                  <div className="absolute bottom-4 left-4 right-4">
                    <div className="h-1 bg-slate-700 rounded-full overflow-hidden">
                      <div className="h-full bg-primary rounded-full" style={{ width: '35%' }} />
                    </div>
                    <div className="flex justify-between text-xs mt-2 text-slate-400">
                      <span>5:23 / {activeLessonData.duration}</span>
                      <span>720p</span>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="aspect-video bg-slate-900 flex items-center justify-center">
                  <div className="text-center">
                    <FileText className="w-16 h-16 text-primary mx-auto mb-4" />
                    <h3 className="text-xl font-semibold mb-2">{activeLessonData.title}</h3>
                    <p className="text-slate-500 mb-4">Complete this quiz to continue</p>
                    <button 
                      onClick={() => setShowQuiz(true)}
                      className="px-6 py-2 bg-primary hover:bg-primary-dark rounded-lg font-medium transition-colors"
                    >
                      Start Quiz
                    </button>
                  </div>
                </div>
              )
            ) : (
              <div className="aspect-video bg-slate-900 flex items-center justify-center">
                <div className="text-center">
                  <BookOpen className="w-16 h-16 text-slate-600 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold mb-2">Select a lesson to start</h3>
                  <p className="text-slate-500">Choose from the lesson list on the right</p>
                </div>
              </div>
            )}
          </div>

          {/* Course Info */}
          <div className="glass rounded-xl p-6">
            <h2 className="text-lg font-semibold mb-3">About this course</h2>
            <p className="text-slate-400 leading-relaxed">{course.description}</p>
            <div className="flex flex-wrap gap-4 mt-4">
              <span className="flex items-center gap-1 text-sm text-slate-500">
                <Clock className="w-4 h-4" /> 48 hours total
              </span>
              <span className="flex items-center gap-1 text-sm text-slate-500">
                <Users className="w-4 h-4" /> {course.totalStudents} students
              </span>
              <span className="flex items-center gap-1 text-sm text-slate-500">
                <Star className="w-4 h-4 text-amber-400" /> {course.rating} rating
              </span>
              <span className="flex items-center gap-1 text-sm text-slate-500">
                <Award className="w-4 h-4" /> Certificate included
              </span>
            </div>
          </div>
        </div>

        {/* Sidebar - Lessons */}
        <div className="space-y-4">
          <div className="glass rounded-xl p-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold">Course Content</h3>
              <span className="text-sm text-slate-500">{course.progress}% complete</span>
            </div>
            <div className="h-2 bg-slate-800 rounded-full overflow-hidden mb-4">
              <div className="h-full bg-primary rounded-full transition-all" style={{ width: `${course.progress}%` }} />
            </div>
            <div className="space-y-1">
              {course.lessons.map((lesson, index) => (
                <button
                  key={lesson.id}
                  onClick={() => !lesson.locked && setActiveLesson(lesson.id)}
                  disabled={lesson.locked}
                  className={`w-full flex items-center gap-3 p-3 rounded-lg transition-colors text-left ${
                    activeLesson === lesson.id ? 'bg-primary/20 border border-primary/30' :
                    lesson.locked ? 'opacity-50 cursor-not-allowed' :
                    lesson.completed ? 'hover:bg-slate-800/50' : 'hover:bg-slate-800'
                  }`}
                >
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${
                    lesson.completed ? 'bg-success/20 text-success' :
                    lesson.locked ? 'bg-slate-800 text-slate-600' :
                    'bg-primary/20 text-primary'
                  }`}>
                    {lesson.completed ? <CheckCircle className="w-4 h-4" /> :
                     lesson.locked ? <Lock className="w-4 h-4" /> :
                     lesson.type === 'video' ? <Play className="w-4 h-4" /> :
                     <FileText className="w-4 h-4" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{index + 1}. {lesson.title}</p>
                    <p className="text-xs text-slate-500">{lesson.duration}</p>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Resources */}
          <div className="glass rounded-xl p-4">
            <h3 className="font-semibold mb-3">Resources</h3>
            <div className="space-y-2">
              {['Course Syllabus PDF', 'Formula Sheet', 'Practice Problems', 'Cheat Sheet'].map((resource, i) => (
                <button key={i} className="w-full flex items-center gap-2 p-2 hover:bg-slate-800 rounded-lg text-sm text-slate-400 hover:text-white transition-colors">
                  <Download className="w-4 h-4" />
                  {resource}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
